import type {
  AutoFillConfig,
  CanvasDataSource,
  CanvasElementProps,
  CanvasElementType,
  ValidationRule,
  VisibilityRules,
} from "../types";
import { resolveElementType } from "../components/canvasBuilder/widgetElementType";
import type { CanvasBuilderElement } from "./canvasBuilderProjection";

type ApiRecord = Record<string, unknown>;

const asRecord = (value: unknown): ApiRecord =>
  value && typeof value === "object" ? (value as ApiRecord) : {};

const asOptionalRecord = (value: unknown): ApiRecord | undefined =>
  value && typeof value === "object" ? (value as ApiRecord) : undefined;

const optionalString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const optionalNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const backendElementTypes: Record<string, CanvasElementType> = {
  FIELD: "field",
  CONTAINER: "container",
  STATIC: "text",
  WIDGET: "widget",
  ACTION_BUTTON: "action_button",
  EMBEDDED_CANVAS: "embedded_canvas",
  VIEW_GRID: "view_grid",
};

export interface CanvasBuilderHydrationResponse {
  elements?: unknown[];
  data_sources?: unknown[];
  embeddings?: unknown[];
}

/** Project the wire-format `/full` response into the builder's working shape. */
export function hydrateCanvasBuilderContent(
  response: CanvasBuilderHydrationResponse,
  generateId: () => string,
): {
  elements: CanvasBuilderElement[];
  dataSources: CanvasDataSource[];
} {
  const rawDataSources = response.data_sources || [];
  const dataSources = rawDataSources.map((rawDataSource) => {
    const dataSource = asRecord(rawDataSource);
    const viewId = optionalNumber(dataSource.view_id);
    const id =
      optionalString(dataSource.alias) ||
      optionalString(dataSource.id) ||
      generateId();

    return {
      id,
      name:
        optionalString(dataSource.alias) ||
        optionalString(dataSource.name) ||
        "Data Source",
      type: "view" as const,
      view_id:
        viewId !== undefined ? viewId : optionalString(dataSource.view_id),
      config: asRecord(dataSource.config_json || dataSource.config),
      is_primary:
        typeof dataSource.is_primary === "boolean"
          ? dataSource.is_primary
          : undefined,
      default_filters: asOptionalRecord(dataSource.default_filters),
      sort_config: asOptionalRecord(dataSource.sort_config),
    };
  });

  const aliasToId: Record<string, string> = {};
  rawDataSources.forEach((rawDataSource, index) => {
    const dataSource = asRecord(rawDataSource);
    const responseId =
      optionalString(dataSource.alias) || optionalString(dataSource.id);
    if (responseId) {
      aliasToId[responseId] = dataSources[index].id;
    }
  });

  const embeddingByElementId: Record<string, ApiRecord> = {};
  (response.embeddings || []).forEach((rawEmbedding) => {
    const embedding = asRecord(rawEmbedding);
    const elementId = embedding.element_id || embedding.elementId;
    if (elementId !== undefined && elementId !== null) {
      embeddingByElementId[String(elementId)] = embedding;
    }
  });

  const elements = (response.elements || []).map((rawElement, index) => {
    const element = asRecord(rawElement);
    const elementId =
      element.id !== undefined && element.id !== null
        ? String(element.id)
        : undefined;
    const builderId = elementId || generateId();
    const backendType = optionalString(element.element_type);
    const configProps = asRecord(element.config_json || element.props);
    const frontendType =
      (backendType === "WIDGET" &&
      resolveElementType("widget", configProps) !== "widget"
        ? (resolveElementType("widget", configProps) as CanvasElementType)
        : undefined) ||
      (backendType && backendElementTypes[backendType]) ||
      (optionalString(element.type) as CanvasElementType | undefined) ||
      "field";
    const rawBinding = asRecord(element.data_binding);
    const bindingAlias =
      optionalString(rawBinding.data_source_alias) ||
      optionalString(rawBinding.data_source_id);
    const dataBinding = bindingAlias
      ? {
          data_source_id: aliasToId[bindingAlias] || bindingAlias,
          field_path: optionalString(rawBinding.field_path) || "",
        }
      : undefined;
    const {
      auto_fill: rawAutoFill,
      validation_rules: rawValidationRules,
      ...baseProps
    } = configProps;
    const embedding = elementId ? embeddingByElementId[elementId] : undefined;
    const embeddingProps =
      frontendType === "embedded_canvas" && embedding
        ? {
            childCanvasId: embedding.child_canvas_id || embedding.childCanvasId,
            parentDataSourceAlias:
              embedding.parent_data_source_alias ||
              embedding.parentDataSourceAlias,
            parentKeyField:
              embedding.parent_key_field || embedding.parentKeyField,
            childDataSourceAlias:
              embedding.child_data_source_alias ||
              embedding.childDataSourceAlias,
            childFilterField:
              embedding.child_filter_field || embedding.childFilterField,
          }
        : {};
    const props =
      frontendType === "embedded_canvas"
        ? { ...embeddingProps, ...baseProps }
        : frontendType === "timeline"
          ? { ...baseProps, ...asRecord(baseProps.timeline_config) }
          : frontendType === "kanban"
            ? { ...baseProps, ...asRecord(baseProps.kanban_config) }
            : frontendType === "calendar"
              ? { ...baseProps, ...asRecord(baseProps.calendar_config) }
              : baseProps;

    return {
      tempId: builderId,
      id: builderId,
      type: frontendType,
      props: props as CanvasElementProps,
      layout: {
        order:
          optionalNumber(element.sort_order) ??
          optionalNumber(element.display_order) ??
          index,
      },
      data_binding: dataBinding,
      gridColumn: optionalNumber(element.grid_column) ?? 1,
      gridRow: optionalNumber(element.grid_row) ?? index + 1,
      columnSpan:
        optionalNumber(element.column_span) ??
        optionalNumber(element.grid_column_span) ??
        12,
      rowSpan:
        optionalNumber(element.row_span) ??
        optionalNumber(element.grid_row_span) ??
        1,
      parentElementId: optionalString(element.parent_element_id),
      parentPanelId: optionalString(element.parent_panel_id),
      displayOrder:
        optionalNumber(element.sort_order) ??
        optionalNumber(element.display_order) ??
        index,
      name: optionalString(element.name),
      isVisible:
        typeof element.is_visible === "boolean"
          ? element.is_visible
          : undefined,
      visibilityRules: asOptionalRecord(element.visibility_rules) as
        VisibilityRules | undefined,
      autoFill: asOptionalRecord(rawAutoFill) as AutoFillConfig | undefined,
      validationRules: Array.isArray(rawValidationRules)
        ? (rawValidationRules as ValidationRule[])
        : [],
      styleOverrides: asOptionalRecord(element.style_overrides),
      cssClasses: optionalString(element.css_classes),
    };
  });

  return {
    elements,
    dataSources,
  };
}
